import { ConversationType } from "@src/types"
import { DATE_FORMATS, URL_STORAGE } from "./constants"
import dayjs from "dayjs"
import { TMediaPayload } from "@pages/Chat/Chat.types"
import { AxiosError } from "axios"

// Date formatting utilities
export const formatDate = (date: string | Date, options?: Intl.DateTimeFormatOptions): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  }

  return dateObj.toLocaleDateString("en-US", { ...defaultOptions, ...options })
}

export const formatDateTime = (date: string | Date): string => {
  try {
    return dayjs(date).format(DATE_FORMATS.DATE_TIME)
  } catch (error) {
    if (typeof date === "string") return date
    return "Invalid date"
  }
}

export const formatRelativeTime = (date: string | Date): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return "a moment ago"
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return ` ${minutes} minute${minutes > 1 ? "s" : ""}`
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return ` ${hours} hour${hours > 1 ? "s" : ""}`
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400)
    return ` ${days} day${days > 1 ? "s" : ""}`
  } else {
    return formatDate(dateObj)
  }
}

// String utilities
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + "..."
}

export const capitalizeFirst = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

// File utilities
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes"

  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}

export const getFileExtension = (filename: string): string => {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2)
}

export const isValidFileType = (file: File, allowedTypes: string[]): boolean => {
  return allowedTypes.includes(file.type)
}

export const isValidFileSize = (file: File, maxSize: number): boolean => {
  return file.size <= maxSize
}

// Array utilities
export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce(
    (groups, item) => {
      const group = String(item[key])
      groups[group] = groups[group] || []
      groups[group].push(item)
      return groups
    },
    {} as Record<string, T[]>,
  )
}

export const sortBy = <T>(array: T[], key: keyof T, direction: "asc" | "desc" = "asc"): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key]
    const bVal = b[key]

    if (aVal < bVal) return direction === "asc" ? -1 : 1
    if (aVal > bVal) return direction === "asc" ? 1 : -1
    return 0
  })
}

// Validation utilities
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const isValidPassword = (password: string): boolean => {
  const minLength = 8
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasNumber = /\d/.test(password)

  return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumber
}

// URL utilities
export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      searchParams.append(key, String(value))
    }
  })

  return searchParams.toString()
}

// Local storage utilities
export const getFromStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error(`Error reading from localStorage:`, error)
    return defaultValue
  }
}

export const setToStorage = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Error writing to localStorage:`, error)
  }
}

export const removeFromStorage = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`Error removing from localStorage:`, error)
  }
}

// Color utilities
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

export const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

// Date utilities
export const calculateAgeByBirthDate = (birthDate: string): number | string => {
  if (!birthDate) return ""
  const today = new Date()
  const birthDateObj = new Date(birthDate)
  let age = today.getFullYear() - birthDateObj.getFullYear()
  const monthDiff = today.getMonth() - birthDateObj.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDateObj.getDate())) {
    age--
  }
  return age
}

export const createId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

// File utilities
export const createFileFromBlob = (blob: Blob, filename: string, type: string): File => {
  return new File([blob], filename, { type })
}

export const getExtensionFromType = (type: ConversationType): string => {
  switch (type) {
    case ConversationType.AUDIO:
      return "mp3"
    case ConversationType.VIDEO:
      return "webm"
    case ConversationType.TEXT:
      return "txt"
  }
  return "mp3"
}

interface IDownloadFileFn {
  (props: { from: "url" | "blob"; ajax?: boolean; url?: string; blob?: Blob }): void | Promise<void>
}

export const downloadFile: IDownloadFileFn = async ({ from = "url", url, blob, ajax = false }) => {
  if (from === "url") {
    if (ajax) {
      const response = await fetch(url!, { mode: "no-cors" })
      const blob = await response.blob()

      const objectUrl = window.URL.createObjectURL(blob)
      const $link = document.createElement("a")
      $link.href = objectUrl
      $link.target = "_blank"
      $link.setAttribute("download", "")
      $link.setAttribute("rel", "noopener")
      document.body.appendChild($link)
      $link.click()
      document.body.removeChild($link)
    } else {
      const $link = document.createElement("a")
      $link.href = url!
      $link.target = "_blank"
      $link.setAttribute("download", "")
      $link.setAttribute("rel", "noopener")
      document.body.appendChild($link)
      $link.click()
      document.body.removeChild($link)
    }
  } else if (from === "blob") {
    const $link = document.createElement("a")
    $link.href = URL.createObjectURL(blob!)
    $link.target = "_blank"
    $link.setAttribute("download", "")
    $link.setAttribute("rel", "noopener")
    document.body.appendChild($link)
    $link.click()
    document.body.removeChild($link)
  }
}

export const fileToBase64 = (file: File): Promise<TMediaPayload> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === "string") {
        const [, base64Data] = reader.result.split(",")
        if (base64Data) {
          resolve({
            mimeType: file.type,
            data: base64Data,
          })
        } else {
          reject(new Error("Invalid Base64 format."))
        }
      } else {
        reject(new Error("Could not read the file."))
      }
    }
    reader.onerror = (error) => reject(error)
    reader.readAsDataURL(file) // To get the Base64
  })
}

export const getMediaTitle = (media: IMedia) => {
  const urlParts = media.storage_url.split("/")
  const filename = urlParts[urlParts.length - 1].split("-").slice(-3).join("-")
  return filename
}

export const isObject = (value: any): boolean => {
  return typeof value === "object" && value !== null && Array.isArray(value) === false
}

export const objectToFormData = (
  obj: Record<string, any>,
  formData = new FormData(),
  parentKey = "",
): FormData => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const fullKey = parentKey ? `${parentKey}[${key}]` : key
      const value = obj[key]
      if (value instanceof Date) {
        formData.append(fullKey, value.toISOString())
      } else if (value instanceof File) {
        formData.append(fullKey, value)
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          const arrayKey = `${fullKey}[${index}]`
          if (isObject(item) || Array.isArray(item)) {
            objectToFormData(item, formData, arrayKey)
          } else {
            formData.append(arrayKey, item)
          }
        })
      } else if (isObject(value)) {
        objectToFormData(value, formData, fullKey)
      } else if (value !== null && value !== undefined) {
        formData.append(fullKey, value)
      }
    }
  }
  return formData
}

export const getFullPath = (path: string | null): string | null => {
  if (!path) return null
  if (!path.startsWith(URL_STORAGE)) {
    return `${URL_STORAGE}${path}`
  }
  return path
}

export const debounceFn = (fn: (...args: any[]) => void, delay: number) => {
  let timeout: NodeJS.Timeout
  return (...args: any[]) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn(...args), delay)
  }
}

export const getPreferredMimeType = (): MediaRecorderOptions | undefined => {
  const preferred = [
    "audio/webm;codecs=opus", // Chrome/Edge/Firefox
    "audio/ogg;codecs=opus", // Firefox
    "audio/mp4", // Safari (AAC)
  ]
  const mimeType = preferred.filter((t) => MediaRecorder.isTypeSupported?.(t))
  if (mimeType.length === 0) return
  return { mimeType: mimeType[0] }
}

type DebouncedFunction<Args extends any[]> = (...args: Args) => void

export function debounce<Args extends any[]>(
  func: DebouncedFunction<Args>,
  delayMs: number,
): DebouncedFunction<Args> {
  let timeoutId: number | undefined

  return function (this: any, ...args: Args) {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }

    timeoutId = window.setTimeout(() => {
      func.apply(this, args)
      timeoutId = undefined
    }, delayMs)
  }
}

export const catchExpressError = (error: unknown, setErrors?: (errors: IExpressError) => void): void => {
  if (error instanceof AxiosError) {
    const errorResponse = error.response
    if (errorResponse) {
      setErrors?.(errorResponse.data as IExpressError)
    }
  }
}
